import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type TextareaHTMLAttributes,
} from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { cn } from '@/lib/cn'

import type { TemplatePreview, TemplateVariable } from '../types'

/**
 * Les outils de l'éditeur de messages : variables, surlignage, aperçu.
 *
 * L'organisateur ne voyait pas quelles variables un message accepte : il
 * réécrivait un texte à l'aveugle, et une variable recopiée d'un autre message
 * partait telle quelle chez le client. Le serveur dit désormais, pour chaque
 * modèle, ce que son envoi fournit ; ces composants le montrent.
 */

/** Même motif que le rendu serveur (`PLACEHOLDER` dans `templates.py`). */
const PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g

export function placeholdersIn(...texts: string[]): string[] {
  const found = new Set<string>()
  for (const text of texts) {
    for (const match of text.matchAll(PLACEHOLDER)) if (match[1]) found.add(match[1])
  }
  return [...found]
}

function describe(variable: TemplateVariable, language: string): string {
  const base = language.slice(0, 2)
  return (
    variable.description[base] ??
    variable.description.fr ??
    Object.values(variable.description)[0] ??
    ''
  )
}

// ── Le champ « Message », variables surlignées ─────────────────────

export interface HighlightedTextareaHandle {
  /** Insère `text` à la place de la sélection, curseur placé après. */
  insert: (text: string) => void
}

/**
 * Une zone de texte dont les `{{variables}}` sont surlignées.
 *
 * Un `<textarea>` ne sait rien colorer : le texte est recopié, à l'identique
 * et en transparent, dans un calque placé dessous, où seules les variables ont
 * un fond. Pour que les deux coïncident au pixel, la zone grandit avec son
 * contenu (pas de barre de défilement interne, qui décalerait les retours à
 * la ligne) et les deux partagent police, marges et règles de coupure.
 */
export const HighlightedTextarea = forwardRef<
  HighlightedTextareaHandle,
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
    value: string
    onValueChange: (value: string) => void
    known: Set<string>
  }
>(function HighlightedTextarea({ value, onValueChange, known, className, ...rest }, ref) {
  const area = useRef<HTMLTextAreaElement>(null)
  const pendingCaret = useRef<number | null>(null)

  useImperativeHandle(ref, () => ({
    insert(text: string) {
      const node = area.current
      const start = node?.selectionStart ?? value.length
      const end = node?.selectionEnd ?? value.length
      pendingCaret.current = start + text.length
      onValueChange(value.slice(0, start) + text + value.slice(end))
    },
  }))

  // La hauteur suit le texte ; le curseur revient juste après l'insertion.
  useLayoutEffect(() => {
    const node = area.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
    if (pendingCaret.current !== null) {
      const caret = pendingCaret.current
      pendingCaret.current = null
      node.focus()
      node.setSelectionRange(caret, caret)
    }
  }, [value])

  // Le calque reprend chaque caractère : un `{{nom}}` connu est doré, un
  // inconnu rouge. Le reste est du texte transparent qui occupe la place.
  const pieces: { text: string; kind: 'plain' | 'known' | 'unknown' }[] = []
  let cursor = 0
  for (const match of value.matchAll(PLACEHOLDER)) {
    const at = match.index ?? 0
    if (at > cursor) pieces.push({ text: value.slice(cursor, at), kind: 'plain' })
    pieces.push({ text: match[0], kind: known.has(match[1] ?? '') ? 'known' : 'unknown' })
    cursor = at + match[0].length
  }
  pieces.push({ text: value.slice(cursor), kind: 'plain' })

  const shared =
    'w-full rounded-md border px-3 py-2.5 font-mono text-sm leading-relaxed ' +
    'whitespace-pre-wrap break-words [overflow-wrap:anywhere]'

  return (
    <div className={cn('relative', className)}>
      <div
        aria-hidden
        className={cn(
          shared,
          'pointer-events-none absolute inset-0 overflow-hidden border-transparent bg-bg-base text-transparent',
        )}
      >
        {pieces.map((piece, index) =>
          piece.kind === 'plain' ? (
            <span key={index}>{piece.text}</span>
          ) : (
            <mark
              key={index}
              className={cn(
                'rounded-sm text-transparent',
                piece.kind === 'known' ? 'bg-champagne-400/20' : 'bg-state-danger/35',
              )}
            >
              {piece.text}
            </mark>
          ),
        )}
        {/* Une dernière ligne vide doit compter dans la hauteur du calque. */}
        {'​'}
      </div>
      <textarea
        ref={area}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        className={cn(
          shared,
          'relative block resize-none overflow-hidden border-line bg-transparent text-content-primary',
        )}
        {...rest}
      />
    </div>
  )
})

// ── Variable inconnue ──────────────────────────────────────────────

/** Ce que l'envoi ne fournira pas : resterait tel quel dans l'e-mail. */
export function UnknownVariablesNote({ names }: { names: string[] }) {
  const { t } = useTranslation()
  if (names.length === 0) return null
  return (
    <div
      role="alert"
      className="mt-3 rounded-md border border-state-danger/40 bg-state-danger/5 px-3 py-3 text-sm"
    >
      <p className="font-medium text-state-danger">{t('admin.messagesVars.unknownTitle')}</p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {names.map((name) => (
          <li key={name}>
            <code className="break-all rounded bg-state-danger/15 px-1.5 py-0.5 font-mono text-xs text-content-primary">
              {`{{${name}}}`}
            </code>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-content-secondary">{t('admin.messagesVars.unknownHint')}</p>
    </div>
  )
}

// ── Le panneau des variables ───────────────────────────────────────

const WIDE = '(min-width: 1280px)'

function isWide(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.(WIDE).matches === true
}

/**
 * La liste des variables du message, chacune avec son sens et un exemple.
 *
 * Ouvert d'office sur grand écran, où il tient à côté de l'éditeur ; replié
 * sur téléphone, où il pousserait le texte à modifier sous la ligne de
 * flottaison.
 */
export function VariablesPanel({
  variables,
  used,
  target,
  onInsert,
  onEditTexts,
}: {
  variables: TemplateVariable[]
  /** Variables déjà citées par le texte. */
  used: Set<string>
  /** Le champ qui recevra l'insertion (« Objet » ou « Message »). */
  target: string
  onInsert: (token: string) => void
  /**
   * Ouvre « Textes des variables » sur le groupe de phrases qui compose une
   * variable : le contenu de `{{order_details}}` ne se réécrit pas dans le
   * modèle, mais phrase par phrase.
   */
  onEditTexts?: (group: string) => void
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(isWide)
  const [copied, setCopied] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  function copy(token: string) {
    // Le presse-papiers exige une page sûre (https) ; à défaut, « Insérer »
    // reste disponible.
    void navigator.clipboard
      ?.writeText(token)
      .then(() => {
        setCopied(token)
        window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => setCopied(null), 2000)
      })
      .catch(() => setCopied(null))
  }

  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      className="min-w-0 rounded-md border border-line-subtle bg-bg-base/40"
      data-testid="variables-panel"
    >
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-content-primary [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1">
          {t('admin.messagesVars.title')}{' '}
          <span className="text-content-muted">({variables.length})</span>
        </span>
        <span aria-hidden className="shrink-0 text-content-muted">
          {open ? '−' : '+'}
        </span>
      </summary>

      <div className="border-t border-line-subtle px-3 py-3">
        {variables.length === 0 ? (
          <p className="text-sm text-content-secondary">{t('admin.messagesVars.none')}</p>
        ) : (
          <>
            <p className="text-sm text-content-muted">{t('admin.messages.variables')}</p>
            <p className="mt-2 text-sm text-content-secondary">
              {t('admin.messagesVars.insertInto', { field: target })}
            </p>
            <ul className="mt-3 space-y-2">
              {variables.map((variable) => {
                const token = `{{${variable.name}}}`
                return (
                  <li
                    key={variable.name}
                    className="min-w-0 rounded-md border border-line-subtle bg-bg-base px-3 py-2.5"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="min-w-0 break-all font-mono text-sm text-champagne-300">
                        {token}
                      </code>
                      {used.has(variable.name) ? (
                        <span className="rounded-full border border-line px-2 py-0.5 text-[10px] uppercase tracking-wide text-content-muted">
                          {t('admin.messagesVars.used')}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-content-secondary">
                      {describe(variable, i18n.language)}
                    </p>
                    <p className="mt-1 line-clamp-4 whitespace-pre-line break-words text-xs text-content-muted [overflow-wrap:anywhere]">
                      <span className="font-medium">{t('admin.messagesVars.example')}</span>{' '}
                      {variable.example || t('admin.messagesVars.emptyExample')}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onInsert(token)}
                        aria-label={t('admin.messagesVars.insertLabel', {
                          name: variable.name,
                          field: target,
                        })}
                      >
                        {t('admin.messagesVars.insert')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => copy(token)}>
                        {copied === token
                          ? t('admin.messagesVars.copied')
                          : t('admin.messagesVars.copy')}
                      </Button>
                      {variable.texts_group && onEditTexts ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onEditTexts(variable.texts_group ?? '')}
                        >
                          {t('admin.messageTexts.editPhrases')}
                        </Button>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>
    </details>
  )
}

// ── Aperçu ─────────────────────────────────────────────────────────

/**
 * Le message tel qu'il partirait, rempli avec des valeurs d'exemple.
 *
 * Le HTML est affiché dans un cadre isolé (`sandbox` vide : ni script, ni
 * formulaire, ni accès à la page) — c'est du texte saisi en back-office, il
 * n'a rien à exécuter ici. La version texte suit : c'est celle que lisent
 * les messageries qui n'affichent pas le HTML.
 */
export function PreviewPanel({
  preview,
  stale,
  onRefresh,
  onClose,
  refreshing,
}: {
  preview: TemplatePreview
  stale: boolean
  onRefresh: () => void
  onClose: () => void
  refreshing: boolean
}) {
  const { t } = useTranslation()
  return (
    <section
      aria-label={t('admin.messagesVars.previewTitle')}
      className="mt-4 min-w-0 rounded-md border border-line bg-bg-base/40 px-3 py-3"
      data-testid="template-preview"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="min-w-0 flex-1 text-sm font-medium text-content-primary">
          {t('admin.messagesVars.previewTitle')}
        </h3>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('admin.messagesVars.previewHide')}
        </Button>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-content-muted">
        {t('admin.messagesVars.previewNote')}
      </p>
      {preview.automatic_blocks ? (
        <p className="mt-1 text-xs leading-relaxed text-content-muted">
          {t('admin.messagesVars.previewBlocks')}
        </p>
      ) : null}

      {stale ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-state-warning/40 bg-state-warning/5 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 text-state-warning">
            {t('admin.messagesVars.previewStale')}
          </span>
          <Button size="sm" variant="outline" disabled={refreshing} onClick={onRefresh}>
            {t('admin.messagesVars.previewRefresh')}
          </Button>
        </div>
      ) : null}

      <p className="mt-3 text-caption uppercase tracking-wide text-content-muted">
        {t('admin.messages.subject')}
      </p>
      <p className="mt-1 break-words text-sm text-content-primary [overflow-wrap:anywhere]">
        {preview.subject}
      </p>

      {preview.body_html ? (
        <>
          <p className="mt-3 text-caption uppercase tracking-wide text-content-muted">
            {t('admin.messagesVars.previewHtml')}
          </p>
          <iframe
            title={t('admin.messagesVars.previewFrame')}
            sandbox=""
            srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0}img{max-width:100%;height:auto}</style></head><body>${preview.body_html}</body></html>`}
            className="mt-1.5 h-[32rem] w-full rounded-md border border-line-subtle bg-white"
          />
        </>
      ) : null}

      <p className="mt-3 text-caption uppercase tracking-wide text-content-muted">
        {t('admin.messagesVars.previewText')}
      </p>
      <pre className="mt-1.5 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md border border-line-subtle bg-bg-base px-3 py-2.5 font-mono text-xs leading-relaxed text-content-secondary [overflow-wrap:anywhere]">
        {preview.body_text}
      </pre>
    </section>
  )
}
